import { Trans } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";

/**
 * Admin → SMS / Messaging dashboard.
 *
 * Port of `src/app/admin/sms/page.tsx` (legacy NextJS, ~285 LOC) per D21.
 *
 * Endpoint: GET /api/admin/sms → SmsStats payload (platform totals + by-country
 * + top entities + billing events + BYOP configs).
 */

interface SmsStats {
  currentMonth: string;
  platform: { total: number; otp: number; reminder: number; cost: number };
  byCountry: { country: string; otp: number; reminder: number; cost: number }[];
  topEntities: {
    entityType: string;
    entityId: string;
    name: string;
    total: number;
    platform: number;
    byop: number;
    quota: number;
  }[];
  billingEvents: {
    id: string;
    entityType: string;
    entityId: string;
    name: string;
    billingMonth: string;
    totalSent: number;
    quotaIncluded: number;
    overageCount: number;
    overageAmount: string;
    status: string;
  }[];
  byopConfigs: {
    id: string;
    entityType: string;
    entityId: string;
    name: string;
    provider: string;
    isActive: boolean;
    isVerified: boolean;
    lastTestedAt: string | null;
  }[];
}

export const Route = createFileRoute("/_authenticated/sms")({
  component: SmsPage,
});

function SmsPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["admin", "sms"],
    queryFn: () => apiFetch.get<SmsStats>("/api/admin/sms"),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Messaging Dashboard</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Platform providers, metering, billing & BYOP</Trans>
        </p>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground p-8">
          <Trans>Loading SMS dashboard...</Trans>
        </div>
      ) : !stats ? (
        <div className="text-muted-foreground p-8">
          <Trans>Failed to load SMS data</Trans>
        </div>
      ) : (
        <>
          <div className="border-b pt-2 pb-1">
            <h2 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
              <Trans>SMS Metering & Billing — {stats.currentMonth}</Trans>
            </h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              labelNode={<Trans>📱 Total SMS (platform)</Trans>}
              value={stats.platform.total}
            />
            <StatCard
              labelNode={<Trans>🔑 OTP (login/register)</Trans>}
              value={stats.platform.otp}
            />
            <StatCard labelNode={<Trans>🔔 Reminders</Trans>} value={stats.platform.reminder} />
            <StatCard
              labelNode={<Trans>💰 Platform cost</Trans>}
              value={`€${stats.platform.cost.toFixed(2)}`}
            />
          </div>

          {stats.byCountry.length > 0 && (
            <div className="bg-card rounded-xl border">
              <div className="border-b px-4 py-3">
                <h2 className="text-sm font-semibold">
                  <Trans>Per country × category</Trans>
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="px-4 py-2 font-semibold">
                        <Trans>Country</Trans>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">🔑 OTP</th>
                      <th className="px-4 py-2 text-center font-semibold">🔔 Reminder</th>
                      <th className="px-4 py-2 text-center font-semibold">💰 Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.byCountry.map((c) => (
                      <tr key={c.country} className="border-b last:border-0">
                        <td className="px-4 py-2 font-medium">{c.country}</td>
                        <td className="px-4 py-2 text-center font-mono">{c.otp}</td>
                        <td className="px-4 py-2 text-center font-mono">{c.reminder}</td>
                        <td className="px-4 py-2 text-center font-mono">€{c.cost.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {stats.topEntities.length > 0 && (
            <div className="bg-card rounded-xl border">
              <div className="border-b px-4 py-3">
                <h2 className="text-sm font-semibold">
                  <Trans>Top SMS users</Trans>
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="px-4 py-2 font-semibold">
                        <Trans>Entity</Trans>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        <Trans>Total</Trans>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        <Trans>Platform</Trans>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">BYOP</th>
                      <th className="px-4 py-2 text-center font-semibold">
                        <Trans>Quota</Trans>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        <Trans>Status</Trans>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topEntities.map((e) => {
                      const overQuota = e.platform > e.quota && e.quota >= 0;
                      return (
                        <tr
                          key={`${e.entityType}:${e.entityId}`}
                          className="border-b last:border-0"
                        >
                          <td className="px-4 py-2">
                            <span className="text-muted-foreground text-[0.625rem] uppercase">
                              {e.entityType}
                            </span>{" "}
                            {e.name}
                          </td>
                          <td className="px-4 py-2 text-center font-mono">{e.total}</td>
                          <td className="px-4 py-2 text-center font-mono">{e.platform}</td>
                          <td className="px-4 py-2 text-center font-mono">{e.byop}</td>
                          <td className="px-4 py-2 text-center font-mono">
                            {e.quota === -1 ? "∞" : e.quota}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {overQuota ? (
                              <Badge className="bg-red-100 text-red-700">
                                <Trans>Overage</Trans>
                              </Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-700">OK</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {stats.billingEvents.length > 0 && (
            <div className="bg-card rounded-xl border">
              <div className="border-b px-4 py-3">
                <h2 className="text-sm font-semibold">
                  <Trans>SMS Billing</Trans>
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="px-4 py-2 font-semibold">
                        <Trans>Entity</Trans>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        <Trans>Month</Trans>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        <Trans>Sent</Trans>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        <Trans>Quota</Trans>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        <Trans>Overage</Trans>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        <Trans>Amount</Trans>
                      </th>
                      <th className="px-4 py-2 text-center font-semibold">
                        <Trans>Status</Trans>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.billingEvents.map((ev) => (
                      <tr key={ev.id} className="border-b last:border-0">
                        <td className="px-4 py-2">
                          <span className="text-muted-foreground text-[0.625rem] uppercase">
                            {ev.entityType}
                          </span>{" "}
                          {ev.name}
                        </td>
                        <td className="px-4 py-2 text-center">{ev.billingMonth}</td>
                        <td className="px-4 py-2 text-center font-mono">{ev.totalSent}</td>
                        <td className="px-4 py-2 text-center font-mono">{ev.quotaIncluded}</td>
                        <td className="px-4 py-2 text-center font-mono">{ev.overageCount}</td>
                        <td className="px-4 py-2 text-center font-mono">€{ev.overageAmount}</td>
                        <td className="px-4 py-2 text-center">
                          <Badge
                            className={
                              ev.status === "paid"
                                ? "bg-green-100 text-green-700"
                                : ev.status === "waived"
                                  ? "bg-muted text-muted-foreground"
                                  : "bg-amber-100 text-amber-700"
                            }
                          >
                            {ev.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {stats.byopConfigs.length > 0 && (
            <div className="bg-card rounded-xl border">
              <div className="border-b px-4 py-3">
                <h2 className="text-sm font-semibold">
                  <Trans>BYOP Configurations</Trans>
                </h2>
              </div>
              <div className="space-y-2 p-4">
                {stats.byopConfigs.map((cfg) => (
                  <div
                    key={cfg.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div>
                      <span className="text-muted-foreground text-[0.625rem] uppercase">
                        {cfg.entityType}
                      </span>{" "}
                      <span className="text-sm font-medium">{cfg.name}</span>
                      <span className="text-muted-foreground ml-2 text-xs">{cfg.provider}</span>
                    </div>
                    <div className="flex gap-2">
                      {cfg.isVerified ? (
                        <Badge className="bg-green-100 text-green-700">
                          <Trans>Verified</Trans>
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700">
                          <Trans>Unverified</Trans>
                        </Badge>
                      )}
                      {cfg.isActive ? (
                        <Badge className="bg-blue-100 text-blue-700">
                          <Trans>Active</Trans>
                        </Badge>
                      ) : (
                        <Badge className="bg-muted text-muted-foreground">
                          <Trans>Inactive</Trans>
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ labelNode, value }: { labelNode: React.ReactNode; value: string | number }) {
  return (
    <div className="bg-card rounded-xl border p-4">
      <div className="text-muted-foreground text-[0.625rem] font-semibold tracking-wider uppercase">
        {labelNode}
      </div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </div>
  );
}
